import { Box, Button, Heading, Text } from '@chakra-ui/react';
import { ReactElement, memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AuthView, useAuthFlow } from '../../hooks/useAuthFlow';
import { AuthWrapperProps } from '../AuthWrapper/AuthWrapper';
import LoginForm from '../Form/Login/LoginForm';
import RegisterForm from '../Form/Register/RegisterForm';
import VerifyForm from '../Form/Verify/VerifyForm';

export type AuthModalWrapperProps = AuthWrapperProps;

const AuthModalWrapper = memo(
  ({ loginHeader, registerHeader, ...props }: AuthModalWrapperProps): ReactElement | null => {
    const { t } = useTranslation();
    const {
      emailRef,
      view,
      formProps,
      updateView,
      onChangeEmailClick,
      onResendClick,
      resendMutation,
    } = useAuthFlow(props);

    const email = emailRef.current;
    if (view === AuthView.VERIFY) {
      return (
        <Box data-id="modal-verify">
          <Heading as="h4" textAlign="left" fontSize="2xl" mb={6}>
            {t(
              resendMutation.isSuccess ? 'auth.checkEmailResentForCode' : 'auth.checkEmailForCode',
            )}
          </Heading>
          <Text mt={2} mb={6}>
            <Trans
              i18nKey={
                resendMutation.isSuccess ? 'auth.verifyResentExplanation' : 'auth.verifyExplanation'
              }
              values={{ email }}
            />
          </Text>
          <VerifyForm
            showResponse
            {...formProps}
            email={email}
            clientId={props?.clientId}
            state={props?.state}
            codeChallenge={props.codeChallenge}
          />
          <Box mt={6} color="gray.700" textAlign="center" fontSize="sm">
            {t('global.havingTrouble')}? {t('global.checkYourSpam')}.
            <Button
              variant="minimal"
              px={1}
              ml={2}
              textDecoration="underline"
              fontWeight="bold"
              onClick={onChangeEmailClick}
              data-id="btn-change-email"
            >
              {t('global.changeEmail')}
            </Button>
            <span> / </span>
            <Button
              variant="minimal"
              px={1}
              textDecoration="underline"
              fontWeight="bold"
              onClick={onResendClick}
              isLoading={resendMutation.isPending}
              data-id="btn-resend"
            >
              {t('global.resend')}
            </Button>
          </Box>
        </Box>
      );
    }
    if (view === AuthView.REGISTER) {
      return (
        <Box data-id="modal-register">
          {registerHeader || (
            <>
              <Heading as="h4" textAlign="left" fontSize="2xl">
                {t('global.joinWithYourWorkEmail')}
              </Heading>
              <Text mt={2} mb={8}>
                <Trans i18nKey="global.joinWithYourWorkEmailSubheading" />
              </Text>
            </>
          )}
          <RegisterForm showResponse withTerms {...formProps} />
          <Box mt={10} color="gray.700" textAlign="center" fontSize="lg" fontWeight="bold">
            {t('global.alreadyJoined')}
            <Button
              ml={2}
              px={2}
              variant="link"
              size="md"
              color="red.900"
              textDecoration="underline"
              fontWeight="bold"
              onClick={() => {
                updateView(AuthView.LOGIN);
              }}
            >
              {t('global.logIn')}
            </Button>
          </Box>
        </Box>
      );
    }
    if (view === AuthView.LOGIN) {
      return (
        <Box data-id="modal-login">
          {loginHeader || (
            <Heading textAlign="left" fontSize="2xl" mb={6} as="h4">
              {t('global.logInTo')}
            </Heading>
          )}
          <LoginForm showResponse {...formProps} />
          <Box mt={10} color="gray.700" textAlign="center" fontSize="lg" fontWeight="bold">
            {t('global.newTo')}
            <Button
              ml={2}
              px={2}
              variant="link"
              size="md"
              color="red.900"
              textDecoration="underline"
              fontWeight="bold"
              data-id="btn-signup"
              onClick={() => {
                updateView(AuthView.REGISTER);
              }}
            >
              {t('global.signUp')}
            </Button>
          </Box>
        </Box>
      );
    }
    return null;
  },
);

export default AuthModalWrapper;
